/**
 * Formats a number into human-readable Indian numbering system
 * @param num - The number to format
 * @param options - Formatting options
 * @returns Formatted string in Indian numbering system
 */
export type NumberFormatterOptions = {
  /** Whether to show full words (e.g., "Crore", "Lakh") or abbreviations (e.g., "Cr", "L") */
  useAbbreviations?: boolean;
  /** Whether to show decimal places for the main unit */
  showDecimals?: boolean;
  /** Number of decimal places to show */
  decimalPlaces?: number;
};

export function formatIndianNumber(
  num: number,
  options: NumberFormatterOptions = {}
): string {
  const {
    useAbbreviations = false,
    showDecimals = false,
    decimalPlaces = 2,
  } = options;

  if (num === 0) return '0';

  const isNegative = num < 0;
  const absNum = Math.abs(Math.floor(num)); // Floor to handle decimal inputs

  // Define units
  const croreUnit = useAbbreviations ? 'Cr' : 'crore';
  const lakhUnit = useAbbreviations ? 'L' : 'lakh';
  const thousandUnit = useAbbreviations ? 'K' : 'thousand';

  let result = '';

  if (absNum >= 10000000) {
    // 1 Crore and above
    const crores = Math.floor(absNum / 10000000);
    const remainder = absNum % 10000000;

    if (showDecimals) {
      const croreValue = absNum / 10000000;
      const spacer = useAbbreviations ? '' : ' ';
      result = `${croreValue.toFixed(decimalPlaces)}${spacer}${croreUnit}`;
    } else {
      const parts = [];
      const spacer = useAbbreviations ? '' : ' ';
      parts.push(`${addCommas(crores)}${spacer}${croreUnit}`);

      if (remainder >= 100000) {
        // Show lakhs if >= 1 lakh
        const lakhs = Math.floor(remainder / 100000);
        const finalRemainder = remainder % 100000;
        parts.push(`${addCommas(lakhs)}${spacer}${lakhUnit}`);

        if (finalRemainder >= 1000) {
          // Break down further into thousands
          const thousands = Math.floor(finalRemainder / 1000);
          const lastRemainder = finalRemainder % 1000;
          parts.push(`${thousands}${spacer}${thousandUnit}`);

          if (lastRemainder > 0) {
            parts.push(lastRemainder.toString());
          }
        } else if (finalRemainder > 0) {
          parts.push(finalRemainder.toString());
        }
      } else if (remainder >= 1000) {
        // Break down into thousands
        const thousands = Math.floor(remainder / 1000);
        const lastRemainder = remainder % 1000;
        parts.push(`${thousands}${spacer}${thousandUnit}`);

        if (lastRemainder > 0) {
          parts.push(lastRemainder.toString());
        }
      } else if (remainder > 0) {
        parts.push(remainder.toString());
      }

      // Join with commas and "and" for the last part
      if (parts.length === 1) {
        result = parts[0];
      } else if (parts.length === 2) {
        result = parts.join(' and ');
      } else {
        result =
          parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1];
      }
    }
  } else if (absNum >= 100000) {
    // 1 Lakh to 99.99 Lakh
    const lakhs = Math.floor(absNum / 100000);
    const remainder = absNum % 100000;

    if (showDecimals) {
      const lakhValue = absNum / 100000;
      const spacer = useAbbreviations ? '' : ' ';
      result = `${lakhValue.toFixed(decimalPlaces)}${spacer}${lakhUnit}`;
    } else {
      const parts = [];
      const spacer = useAbbreviations ? '' : ' ';
      parts.push(`${addCommas(lakhs)}${spacer}${lakhUnit}`);

      if (remainder >= 1000) {
        // Break down into thousands
        const thousands = Math.floor(remainder / 1000);
        const lastRemainder = remainder % 1000;
        parts.push(`${thousands}${spacer}${thousandUnit}`);

        if (lastRemainder > 0) {
          parts.push(lastRemainder.toString());
        }
      } else if (remainder > 0) {
        parts.push(remainder.toString());
      }

      // Join with commas and "and" for the last part
      if (parts.length === 1) {
        result = parts[0];
      } else if (parts.length === 2) {
        result = parts.join(' and ');
      } else {
        result =
          parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1];
      }
    }
  } else if (absNum >= 1000) {
    // 1 Thousand to 99,999
    const thousands = Math.floor(absNum / 1000);
    const remainder = absNum % 1000;

    if (showDecimals) {
      const thousandValue = absNum / 1000;
      const spacer = useAbbreviations ? '' : ' ';
      result = `${thousandValue.toFixed(decimalPlaces)}${spacer}${thousandUnit}`;
    } else {
      const parts = [];
      const spacer = useAbbreviations ? '' : ' ';
      parts.push(`${addCommas(thousands)}${spacer}${thousandUnit}`);

      if (remainder > 0) {
        parts.push(remainder.toString());
      }

      // Join with "and"
      result = parts.join(' and ');
    }
  } else {
    result = addCommas(absNum).toString();
  }

  return isNegative ? `-${result}` : result;
}

/**
 * Adds commas to a number following Indian numbering system
 * @param num - Number to add commas to
 * @returns String with commas added
 */
function addCommas(num: number): string {
  const numStr = num.toString();

  // For numbers less than 1000, no commas needed
  if (num < 1000) return numStr;

  // Indian numbering system: first comma after 3 digits from right, then every 2 digits
  const reversed = numStr.split('').reverse();
  const result: string[] = [];

  for (let i = 0; i < reversed.length; i++) {
    if (i === 3 || (i > 3 && (i - 3) % 2 === 0)) {
      result.push(',');
    }
    result.push(reversed[i]);
  }

  return result.reverse().join('');
}

/**
 * Formats currency in Indian Rupees with proper formatting
 * @param amount - Amount to format
 * @param options - Formatting options
 * @returns Formatted currency string
 */
export function formatIndianCurrency(
  amount: number,
  options: NumberFormatterOptions = {}
): string {
  const formattedNumber = formatIndianNumber(amount, options);
  return `₹${formattedNumber}`;
}

/**
 * Gets a short format for tooltips and compact displays
 * @param num - Number to format
 * @param showDecimals - Whether to show decimals
 * @returns Short formatted string
 */
export function formatIndianNumberShort(
  num: number,
  showDecimals = true
): string {
  return formatIndianNumber(num, {
    useAbbreviations: true,
    showDecimals,
    decimalPlaces: 1,
  });
}

/**
 * Gets a detailed format for full displays
 * @param num - Number to format
 * @returns Detailed formatted string
 */
export function formatIndianNumberDetailed(num: number): string {
  return formatIndianNumber(num, {
    useAbbreviations: false,
    showDecimals: false,
  });
}

/**
 * Formats a number as currency using Intl.NumberFormat
 * @param amount - Amount to format
 * @returns Formatted currency string (e.g., "₹50,00,000.0")
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(amount);
}
